/**
 * useTestCaseList
 *
 * Fetches test cases from GET /api/test-cases with all Phase 1 filter params.
 * Applies client-side search + filter from the Zustand store on top of the
 * server-side result so that filter changes are instant without extra API calls.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { useTestCasesStore } from '../../../store/useTestCasesStore';
import type { ITestCaseRow } from '../types';

const API_URL = API_BASE_URL;

interface UseTestCaseListOptions {
    projectId:  string | null;
    folderId?:  string | null;
}

export function useTestCaseList({ projectId, folderId }: UseTestCaseListOptions) {
    const { token } = useAuth();
    const queryClient = useQueryClient();

    const searchQuery = useTestCasesStore((s) => s.searchQuery);
    const filters     = useTestCasesStore((s) => s.filters);

    // Build server-side query params (folder + project scope)
    const serverParams = useMemo(() => {
        const p = new URLSearchParams();
        if (projectId) p.set('projectId', projectId);
        if (folderId)  p.set('folderId',  folderId);
        return p.toString();
    }, [projectId, folderId]);

    const queryKey = ['test-cases', projectId ?? 'all', folderId ?? 'all', token];

    const { data, isLoading, isError } = useQuery<{ testCases: ITestCaseRow[] }>({
        queryKey,
        queryFn: async () => {
            const qs  = serverParams ? `?${serverParams}` : '';
            const url = `${API_URL}/api/test-cases${qs}`;
            const { data } = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return data.data;
        },
        enabled: !!token,
        staleTime: 30_000,
    });

    const allTestCases: ITestCaseRow[] = data?.testCases ?? [];

    // Client-side filtering on top of server result
    const testCases = useMemo(() => {
        let list = allTestCases;

        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            list = list.filter(
                (tc) =>
                    tc.title.toLowerCase().includes(q) ||
                    tc.tcId?.toLowerCase().includes(q) ||
                    tc.tags?.some((t) => t.toLowerCase().includes(q)),
            );
        }

        if (filters.type) {
            list = list.filter((tc) => tc.type === filters.type);
        }
        if (filters.severity?.length) {
            list = list.filter((tc) => tc.severity && filters.severity!.includes(tc.severity));
        }
        if (filters.caseType?.length) {
            list = list.filter((tc) => tc.caseType && filters.caseType!.includes(tc.caseType));
        }
        if (filters.tags?.length) {
            list = list.filter((tc) =>
                filters.tags!.every((tag) => tc.tags?.includes(tag)),
            );
        }

        return list;
    }, [allTestCases, searchQuery, filters]);

    function invalidate() {
        queryClient.invalidateQueries({ queryKey: ['test-cases'] });
    }

    async function deleteTestCase(id: string) {
        await axios.delete(`${API_URL}/api/test-cases/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        invalidate();
    }

    async function bulkDelete(ids: string[]) {
        await axios.delete(`${API_URL}/api/test-cases/bulk`, {
            data: { ids },
            headers: { Authorization: `Bearer ${token}` },
        });
        invalidate();
    }

    async function moveToFolder(ids: string[], targetFolderId: string) {
        await axios.patch(
            `${API_URL}/api/test-cases/move`,
            { ids, targetFolderId },
            { headers: { Authorization: `Bearer ${token}` } },
        );
        invalidate();
    }

    async function bulkPatch(ids: string[], patch: Record<string, unknown>) {
        await axios.patch(
            `${API_URL}/api/test-cases/bulk-patch`,
            { ids, patch },
            { headers: { Authorization: `Bearer ${token}` } },
        );
        invalidate();
    }

    /**
     * Reorder: moves `activeId` before or after `overId` in the full (unfiltered) list,
     * updates the React Query cache optimistically, then persists to the backend.
     * Rolls back the cache on network failure.
     */
    async function reorder(activeId: string, overId: string, position: 'before' | 'after') {
        const snapshot = queryClient.getQueryData<{ testCases: ITestCaseRow[] }>(queryKey);
        if (!snapshot) return;

        // Work on the full server-fetched list so non-visible (filtered-out) items
        // retain their relative order and receive correct sortOrder values.
        const base = snapshot.testCases;
        const fromIdx = base.findIndex((tc) => tc._id === activeId);
        if (fromIdx === -1) return;

        const without  = base.filter((tc) => tc._id !== activeId);
        let insertAt   = without.findIndex((tc) => tc._id === overId);
        if (insertAt === -1) return;
        if (position === 'after') insertAt += 1;

        const reordered = [
            ...without.slice(0, insertAt),
            base[fromIdx],
            ...without.slice(insertAt),
        ];

        // Optimistic: update the cache immediately so the UI moves the row now.
        queryClient.setQueryData(queryKey, { testCases: reordered });

        // Persist: send every item in the folder with its new sortOrder.
        const items = reordered.map((tc, idx) => ({ id: tc._id, sortOrder: idx }));
        try {
            await axios.patch(
                `${API_URL}/api/test-cases/reorder`,
                { items },
                { headers: { Authorization: `Bearer ${token}` } },
            );
        } catch {
            // Network failure — roll back to what the server last told us.
            queryClient.setQueryData(queryKey, snapshot);
        }
    }

    return {
        testCases,
        allTestCases,
        isLoading,
        isError,
        invalidate,
        deleteTestCase,
        bulkDelete,
        moveToFolder,
        bulkPatch,
        reorder,
    };
}
